import { dismissToasts } from '@pypestream/design-system';
import { i18n } from '@pypestream/translations';

// From its defining module, not the modal's barrel: the barrel pulls in the
// React modal, and this is the same pure decision `publish-modal.tsx` makes.
import { resolvePublishBlock } from '../components/modals/publish-modal/node-number-publish-block';
import { tableStore, ValidationMode } from '../stores/table-store';
import { proStudioService, sendProStudioEvent } from '../xstate/app.xstate';
import { sendUserMessage } from '../xstate/app.xstate-utils';
import type { BotType } from '../xstate/prostudio.api-service.types';
import type {
  ProStudioContext,
  ProStudioEvents,
} from '../xstate/prostudio.xstate.types';
import {
  captureAnalyticsEvent,
  classifyAnalyticsError,
  MAX_ANALYTICS_ERROR_MESSAGE_LENGTH,
  solutionAnalyticsProps,
  type SolutionAnalyticsProps,
} from './analytics';
import {
  findOpenSolution,
  openSolutionKey,
} from './auto-publish-announcements';
import { resolveBobcatAccess } from './bobcat-flags';
import { DEFAULT_SAVE_SETTLEMENT_TIMEOUT_MS } from './create-save-settlement';
import {
  beginLocalPublishAttempt,
  clearLocalPublishAttempt,
  markLocalPublishAccepted,
  PUBLISH_IN_PROGRESS_TOAST_ID,
} from './publish-status';
import { selectOpenSolutionId } from './select-open-solution-id';

/**
 * Project Bobcat — auto-publish a brand-new solution's first version, so it
 * is testable straight away instead of waiting on an explicit Publish click.
 *
 * Fires exactly ONCE per solution, at the moment its version FIRST becomes
 * publish-ready — see `tableStore.pendingAutoPublish` / `consumePendingAutoPublish`
 * for how that moment is identified. It is not part of the October release
 * and nothing turns it on (`BOBCAT_UNRELEASED`, PSTUDIO-4636).
 *
 * Deliberately a STANDALONE implementation, not a refactor of
 * `publish-modal.tsx`'s `onPublish` — that file is shipped, has its own e2e
 * suite, and drives real customer publishes; this only ever needs a SUBSET
 * of its behavior (no modal to close, no environment/survey-bot selection
 * UI), and touching the modal to share code risks the one thing Bobcat rules
 * out: breaking shipped behavior. Both call the same underlying primitives
 * (`sendProStudioEvent('publishBot', …)`, `tableStore`, `watch-publish-status`),
 * so there is nothing to keep in sync beyond those. Like the modal, it leaves
 * COMPLETION to the router-level `usePublishStatusWatcherForCoworkers`
 * (PSTUDIO-3985): that watcher is scoped to the loaded version, lifts the
 * publish lock and announces success, so a second watcher here would only
 * risk running the global restore against another solution.
 *
 * Always targets `env: 'sandbox'` — the same non-production environment the
 * Publish modal itself defaults `selectedEnv` to. `bots` is always `[]`:
 * that parameter only matters for survey-type solutions (`computeSeededBots`
 * in `publish-modal.utils.ts`), and this only ever runs for solutions created
 * by the standard Create Solution wizard, which never produces a survey bot
 * (`create-bot.ts` never sets `botType: 'survey'`) — hence `botType: 'main'`
 * is hardcoded at the call site rather than read from context.
 *
 * Before sending, it awaits `tableStore.waitForPendingSaves()`, the same wait
 * `publish-modal.tsx`'s `onPublish` does: node saves still queued are
 * committed first, so the backend compiles a version that includes them
 * rather than one a moment stale. After the wait it runs the modal's
 * pre-publish gate, `resolvePublishBlock`, for the same reason the modal does:
 * the wait is a window in which a coworker merge, a cross-tab reload or a
 * deferred panel commit can rewrite `rows`. Then it uploads the template and
 * waits for every template upload in flight (PSTUDIO-4578), see
 * `autoPublishSolutionVersion`.
 *
 * Every exit that tells the person this publish did not happen BEFORE
 * `publishBot` is sent captures one `solution_published_failed` here
 * (`capturePrePublishFailure`); every exit after it is captured by the
 * `publishBot` invoke itself (`publish-bot-version.ts`), so each attempt is
 * counted once.
 */
export const AUTO_PUBLISH_ENV = 'sandbox';

/**
 * How long the attempt waits for pending node saves before giving up.
 *
 * Both save queues debounce at 0 ms (`saveRowsToDatabaseQueue` and
 * `saveRowOrdersToDatabaseQueue` in `table-store.ts`), and the client those
 * saves go through (`proStudioClient` in `packages/api-services/urql.ts`) sets
 * no abort signal or timeout in its `fetchOptions`, so everything this wait
 * spends is network round trips — a save that never settles holds it forever, together with the
 * publish lock and a toast that does not close. There is no
 * measured upper bound for a healthy save to derive a number from; this reuses
 * the repo's existing backstop for a save that settles neither way,
 * `DEFAULT_SAVE_SETTLEMENT_TIMEOUT_MS` (channel saves, PSTUDIO-3897), rather
 * than inventing a second one. It is a backstop, not an estimate.
 */
export const AUTO_PUBLISH_SAVE_WAIT_MS = DEFAULT_SAVE_SETTLEMENT_TIMEOUT_MS;

const TOAST_KEY = 'pro-studio/navigation:auto_publish.toast';

type AutoPublishToast =
  | 'in_progress_header'
  | 'in_progress_text'
  | 'save_failed'
  | 'save_wait_timed_out'
  | 'publish_failed'
  | 'not_published_after_leaving';

/**
 * Resolved when the message is SHOWN, never at module load — the rule
 * `showToastMessage` in `app.xstate-utils.tsx` follows for its `i18nKey`, and
 * through the same imperative `i18n` singleton, since this code has no React.
 */
const toastText = (
  key: AutoPublishToast,
  defaultValue: string,
  options: { name?: string; location?: string } = {}
): string =>
  i18n.t(`${TOAST_KEY}.${key}`, { defaultValue, ...options }) ?? defaultValue;

export { openSolutionKey };

/**
 * Where a person publishes by hand, for messages that send them there. With
 * the five-stage nav there is no Versions page (the solution root opens
 * Design) and Publish lives in Deploy (PSTUDIO-4457).
 */
export const publishLocation = (): string =>
  resolveBobcatAccess('fiveStageNav', tableStore.featureFlags) === 'allowed'
    ? (i18n.t('pro-studio/navigation:auto_publish.location.deploy', {
        defaultValue: 'Deploy',
      }) ?? 'Deploy')
    : (i18n.t('pro-studio/navigation:auto_publish.location.versions_page', {
        defaultValue: 'the Versions page',
      }) ?? 'the Versions page');

/**
 * Uploads the open draft's rows as its Bot Manager template, the step that
 * makes it publishable. True only for an upload the server accepted.
 */
const uploadTemplate = async ({
  customerId,
  botName,
  draftVersion,
  project,
}: ProStudioContext): Promise<boolean> => {
  if (!customerId || !botName || !draftVersion || !project?.id) return false;
  const result = await tableStore.upload({
    customerId,
    botName,
    draftVersion,
    projectId: project.id,
  });
  // No result: a v2 (Agent Manager) solution, whose repo carries no bot.csv,
  // so there is no template to wait for (see `tableStore.upload`).
  return !result || result.errors.length === 0;
};

/**
 * The name the person knows the open solution by, for a message shown after
 * they may have left it. Looked up through `findOpenSolution`, the lookup
 * `openSolutionKey` uses, so the name and the identity cannot describe
 * different solutions.
 */
const openSolutionName = (
  ctx: Pick<ProStudioContext, 'botName' | 'solutions'>
): string => findOpenSolution(ctx)?.displayName || ctx.botName || '';

/** Is the solution this attempt was started for still the one open? */
const isStillOpen = (attemptSolutionId: string | null): boolean =>
  openSolutionKey(proStudioService.getSnapshot().context) === attemptSolutionId;

/**
 * Undo what the attempt raised: the local publish attempt, the publish lock and
 * the in-progress toast. Only for the solution the attempt was started for —
 * callers check `isStillOpen` first, because after a switch the lock and the
 * toast belong to the solution now open. Publishing never locks the editor
 * (the publish modal does not either), so there is no table mode to restore.
 */
const releaseAttemptUi = (localAttemptId: string | null): void => {
  clearLocalPublishAttempt(localAttemptId);
  tableStore.setSolutionVersionPublishingInProgress(false);
  dismissToasts(PUBLISH_IN_PROGRESS_TOAST_ID);
};

/**
 * One `solution_published_failed` for an exit that refuses BEFORE `publishBot`
 * is sent, in the shape `publish-bot-version.ts`'s own `captureFailure` sends
 * (that one is a closure inside a shipped invoke, so it cannot be shared).
 * `failure_stage: 'guard'` is the existing value for a refusal before the
 * mutation; `error_message` says which refusal it was. `props` are taken
 * before the wait, so a switch during it cannot attribute the attempt to the
 * solution opened afterwards.
 */
const capturePrePublishFailure = (
  props: SolutionAnalyticsProps,
  reason: string
): void => {
  const message = reason.slice(0, MAX_ANALYTICS_ERROR_MESSAGE_LENGTH);
  captureAnalyticsEvent('solution_published_failed', {
    ...props,
    attempted_version_id: null,
    publish_environment: AUTO_PUBLISH_ENV,
    republish: false,
    failure_stage: 'guard',
    error_kind: classifyAnalyticsError(message),
    error_message: message,
    error_id: null,
  });
};

type SaveWaitOutcome = 'saved' | 'save-failed' | 'timed-out';

/**
 * `waitForPendingSaves()` raced against `AUTO_PUBLISH_SAVE_WAIT_MS`. On a
 * timeout the saves keep running and may still land; the race only stops this
 * attempt from waiting on them. Its rejection is still handled (by the
 * `.then` below), so a late failure raises no unhandled rejection.
 */
const waitForSavesWithin = async (ms: number): Promise<SaveWaitOutcome> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<SaveWaitOutcome>((resolve) => {
    timer = setTimeout(() => resolve('timed-out'), ms);
  });
  try {
    return await Promise.race([
      tableStore.waitForPendingSaves().then(
        (): SaveWaitOutcome => 'saved',
        (): SaveWaitOutcome => 'save-failed'
      ),
      timedOut,
    ]);
  } finally {
    clearTimeout(timer);
  }
};

/**
 * One attempt, no retries (ported from project-bobcat, Gary, 2026-10-01).
 * Retrying automatically was tried and taken out: every way of retrying had to
 * guess whether the person had since left, come back or published themselves,
 * and each guess left a way to land a publish on top of theirs. A failure says
 * so, and they publish by hand.
 *
 * The template is uploaded after the saves and the gate, and the publish waits
 * until no template upload is in flight (PSTUDIO-4578). Bot Manager refuses,
 * with 412, to publish a version whose template has not landed, and a
 * brand-new solution's first upload otherwise races this publish:
 * `validateAndSync` uploads it on its own schedule after the version loads.
 */
export const autoPublishSolutionVersion = async ({
  draftVersionId,
  botType,
}: {
  draftVersionId: string;
  botType: BotType;
}): Promise<void> => {
  const attemptCtx = proStudioService.getSnapshot().context;
  const attemptSolutionId = openSolutionKey(attemptCtx);
  // Taken before the wait: after a switch, context describes the solution
  // opened since, and both the message and the analytics must name this one.
  const attemptSolutionName = openSolutionName(attemptCtx);
  const analyticsProps = solutionAnalyticsProps(attemptCtx);

  // The same attempt bookkeeping the publish modal does, so
  // `usePublishStatusWatcherForCoworkers` reports this publish's progress and
  // outcome even when the backend's lock comes and goes too fast to be seen.
  const localAttemptId = selectOpenSolutionId(attemptCtx);
  beginLocalPublishAttempt({
    solutionId: localAttemptId,
    rows: attemptCtx.solutionDeployments,
  });
  tableStore.setSolutionVersionPublishingInProgress(true);
  sendUserMessage({
    type: 'info',
    header: toastText('in_progress_header', 'Publishing to Sandbox'),
    text: toastText(
      'in_progress_text',
      "We'll let you know the moment it's ready to test."
    ),
    toastId: PUBLISH_IN_PROGRESS_TOAST_ID,
    autoClose: false,
  });

  // Once the attempt has been reported, or `publishBot` handed to the machine
  // (whose invoke then reports it), a later throw is logged and nothing more:
  // reporting it again would count the attempt twice.
  let concluded = false;

  /** A refusal before `publishBot` is sent: release the attempt and say so. */
  const refuse = (reason: string, text: string): void => {
    concluded = true;
    capturePrePublishFailure(analyticsProps, reason);
    releaseAttemptUi(localAttemptId);
    sendUserMessage({ type: 'error', text, autoClose: 5000 });
  };
  const publishFailedText = () =>
    toastText(
      'publish_failed',
      "We couldn't publish this solution automatically. Publish it from {{location}} when you're ready.",
      { location: publishLocation() }
    );

  // The person moved to another solution during a wait. Publishing anyway is
  // not an option: the `publishBot` invoke reads `botName`, `selectedBot` and
  // `solutions` from the CURRENT context (`publish-bot-version.ts`), so it
  // would publish this version under the other solution's identity. And the
  // lock, toast and table mode now belong to that other solution, so none of
  // them is touched. What is left is to say so — naming the solution, since it
  // is no longer on screen.
  const leftDuring = (what: string): boolean => {
    if (isStillOpen(attemptSolutionId)) return false;
    concluded = true;
    clearLocalPublishAttempt(localAttemptId);
    capturePrePublishFailure(
      analyticsProps,
      `Open solution changed while ${what}`
    );
    sendUserMessage({
      type: 'error',
      text: toastText(
        'not_published_after_leaving',
        "{{name}} wasn't published automatically. Publish it from {{location}}.",
        { name: attemptSolutionName, location: publishLocation() }
      ),
      autoClose: 5000,
    });
    return true;
  };

  const publishEvent: ProStudioEvents = {
    type: 'publishBot',
    payload: {
      env: AUTO_PUBLISH_ENV,
      version: draftVersionId,
      botType,
      bots: [],
      onPublishComplete: (result) => {
        const failed = result.status === 'error';

        // The person moved to another solution while this was in flight: the
        // lock and the in-progress toast now belong to THAT solution (perhaps
        // to a coworker's publish of it). Touch neither; only this attempt's
        // own record goes.
        if (!isStillOpen(attemptSolutionId)) {
          if (failed) clearLocalPublishAttempt(localAttemptId);
          return;
        }

        // Not captured here: the invoke already sent `solution_published_failed`
        // for this attempt before calling back.
        if (failed) {
          releaseAttemptUi(localAttemptId);
          sendUserMessage({
            type: 'error',
            text: publishFailedText(),
            autoClose: 5000,
          });
          return;
        }

        // Completion (lock release, success toast) is announced by
        // `usePublishStatusWatcherForCoworkers` off the deployment rows. With no
        // version for it to follow, release what this raised here.
        if (!result.solutionVersionId) {
          releaseAttemptUi(localAttemptId);
          return;
        }
        // Accepted: from here the watcher waits for a sign of this attempt.
        markLocalPublishAccepted(localAttemptId);
      },
    },
  };

  try {
    // The same solution can get a new draft during a wait (a reload, or a
    // coworker's publish). The template would then be the new draft's and the
    // publish the old one's, so this attempt stops instead.
    const draftMoved = (): boolean => {
      if (tableStore.draftVersion === draftVersionId) return false;
      refuse('Draft changed before publish', publishFailedText());
      return true;
    };

    const outcome = await waitForSavesWithin(AUTO_PUBLISH_SAVE_WAIT_MS);
    if (leftDuring(`waiting for pending saves (${outcome})`)) return;
    if (draftMoved()) return;

    if (outcome !== 'saved') {
      const timedOut = outcome === 'timed-out';
      refuse(
        timedOut
          ? `Pending saves timed out after ${AUTO_PUBLISH_SAVE_WAIT_MS} ms`
          : 'Pending saves failed before publish',
        timedOut
          ? toastText(
              'save_wait_timed_out',
              "Your latest changes are taking too long to save, so this solution wasn't published automatically. Publish it from {{location}} when you're ready.",
              { location: publishLocation() }
            )
          : toastText(
              'save_failed',
              "We couldn't save your latest changes, so this solution wasn't published automatically. Publish it from {{location}} when you're ready.",
              { location: publishLocation() }
            )
      );
      return;
    }

    // The modal's pre-publish gate, read AFTER the wait for the reason the
    // modal reads it there: the wait is when a coworker merge, a cross-tab
    // reload or a deferred panel commit can rewrite `rows` or end a validation
    // pass without a verdict. Same message keys as the modal, since it is the
    // same refusal.
    const block = resolvePublishBlock(
      tableStore.rowsForValidation,
      tableStore.validationFailure
    );
    if (block) {
      refuse(
        `Publish blocked by validation: ${block.key}`,
        i18n.t(`pro-studio/navigation:${block.key}`, {
          defaultValue: block.defaultValue,
        }) ?? block.defaultValue
      );
      // The modal's way out: a forced pass lights up the offending cells. The
      // modal awaits it before releasing the lock; here the lock is released
      // first, because awaiting would reopen a window in which the person can
      // switch solutions and this attempt would then release THEIR lock. A
      // rejection is swallowed: the person has already been told why this
      // attempt stopped, and a second message about the pass would not help.
      void tableStore
        .validateAndSync(ValidationMode.Auto)
        .catch(() => undefined);
      return;
    }

    const uploaded = await uploadTemplate(
      proStudioService.getSnapshot().context
    );
    await tableStore.templateUploadsSettled();
    if (leftDuring('uploading the template')) return;
    if (draftMoved()) return;
    if (!uploaded) {
      refuse('Template upload refused before publish', publishFailedText());
      return;
    }
    // `publishBot` is only taken while the machine is idle. Sent anywhere else
    // it is dropped, and the lock and the toast above would stay up.
    if (!proStudioService.getSnapshot().can(publishEvent)) {
      refuse('Machine could not take publishBot', publishFailedText());
      return;
    }
    concluded = true;
    sendProStudioEvent(publishEvent);
  } catch (error) {
    console.error('[autoPublish] attempt threw:', error);
    if (concluded) return;
    const reason = `Threw before publish: ${(error as Error)?.message ?? error}`;
    if (!leftDuring(`the attempt threw (${reason})`)) {
      refuse(reason, publishFailedText());
    }
  }
};
